import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/auth';
import { Button, Card, CardHeader, CardContent, Input, Modal, Field, FieldLabel, FieldError, PhoneField } from '../../components/ui';
import { AlertTriangle, Loader2, Sparkles } from 'lucide-react';
import { userService } from '../../services/user';
import { uploadUserAvatar } from '../../services/storage';
import { AvatarUpload } from '../../components/AvatarUpload';
import { useNavigate } from 'react-router-dom';
import { profileSchema, type ProfileFormData } from './settingsSchemas';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { FEATURE_FLAG_KEYS, hasAnyRole } from '@court-sync/shared';
import type { AiAvatarOptionsResponse, GenerateAiAvatarRequest } from '../../services/user';
import { SettingsAppearanceSection } from './SettingsAppearanceSection';
import { academyProfileService } from '../../services/academyProfile';
import { useAcademySlug } from '../../hooks/useAcademySlug';

export default function SettingsProfileTab() {
  const academySlug = useAcademySlug();
  const { user, updateUser, logout } = useAuthStore();
  const [loading, setLoading] = React.useState(false);
  const [aiGenerating, setAiGenerating] = React.useState(false);
  const [showAiAvatarModal, setShowAiAvatarModal] = React.useState(false);
  const [aiOptions, setAiOptions] = React.useState<AiAvatarOptionsResponse | null>(null);
  const [aiSelection, setAiSelection] = React.useState<GenerateAiAvatarRequest>({
    gender: 'female',
    pose: 'forehand',
    outfit: 'classic_white',
    background: 'hard_court_day',
    mood: 'focused',
  });
  const { enabled: aiAvatarsEnabled } = useFeatureFlag(FEATURE_FLAG_KEYS.AI_AVATARS);
  const navigate = useNavigate();

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [confirmEmail, setConfirmEmail] = React.useState('');

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
    },
  });

  const usesAcademyProfile =
    Boolean(academySlug) && Boolean(user && hasAnyRole(user, ['guardian', 'student']));

  React.useEffect(() => {
    if (!usesAcademyProfile || !academySlug) return;

    void (async () => {
      try {
        const res = await academyProfileService.getMyProfiles();
        const row = res.data?.find((p) => p.academy?.slug === academySlug);
        if (!row) return;

        form.reset({
          firstName: row.firstName || user?.firstName || '',
          lastName: row.lastName || user?.lastName || '',
          email: row.email || user?.email || '',
          phone: row.phone || user?.phone || '',
        });
      } catch {
        // Fall back to platform user fields already in the form
      }
    })();
  }, [academySlug, form, user, usesAcademyProfile]);

  const handleSubmit = async (data: ProfileFormData) => {
    try {
      setLoading(true);
      await updateUser(data);
      if (usesAcademyProfile && academySlug) {
        await academyProfileService.updateProfile(academySlug, {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
        });
      }
      toast.success('Profile updated successfully!');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update profile';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmEmail !== user?.email) {
      toast.error('Email confirmation does not match');
      return;
    }

    setDeleting(true);
    try {
      // We'll use a new authApi method or the profile DELETE endpoint
      await userService.deleteAccount();
      toast.success('Your account and all associated data have been deleted.');
      await logout();
      const loginPath = academySlug ? `/academy/${academySlug}/login` : '/login';
      navigate(loginPath, { replace: true });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || error?.message || 'Failed to delete account');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  React.useEffect(() => {
    if (!aiAvatarsEnabled) return;
    userService
      .getAiAvatarOptions()
      .then((response) => {
        if (response.success && response.data) {
          setAiOptions(response.data);
        }
      })
      .catch(() => {
        // Keep section hidden if options fail to load
      });
  }, [aiAvatarsEnabled]);

  const handleGenerateAiAvatar = async () => {
    if (!user) return;
    try {
      setAiGenerating(true);
      const response = await userService.generateAiAvatar(aiSelection);
      const avatarUrl = response.data?.avatarUrl;
      if (!avatarUrl) {
        throw new Error('No avatar was generated. Please try again.');
      }
      await updateUser({ avatarUrl });
      const remaining = response.data?.rateLimit?.remaining;
      toast.success(
        typeof remaining === 'number'
          ? `AI avatar generated. ${remaining} generations left in this window.`
          : 'AI avatar generated successfully!'
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.error || error?.message || 'Failed to generate AI avatar');
    } finally {
      setAiGenerating(false);
    }
  };

  return (
    <>
      <SettingsAppearanceSection />
    <Card>
      <CardHeader>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Profile Information
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Update your personal information and contact details
        </p>
      </CardHeader>
      <CardContent>
        {user && (
          <div className="flex justify-center mb-6">
            <AvatarUpload
              avatarUrl={user.avatarUrl}
              firstName={user.firstName}
              lastName={user.lastName}
              onUpload={async (file) => {
                const url = await uploadUserAvatar(file, user.uid);
                return url;
              }}
              onSave={async (avatarUrl) => {
                await updateUser({ avatarUrl });
              }}
              onRemove={async () => {
                await updateUser({ avatarUrl: null });
              }}
              middleContent={
                aiAvatarsEnabled ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-2"
                    onClick={() => setShowAiAvatarModal(true)}
                    disabled={!aiOptions}
                  >
                    <Sparkles className="h-4 w-4" />
                    AI Avatar (Beta)
                  </Button>
                ) : null
              }
              size="xl"
            />
          </div>
        )}
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field data-invalid={!!form.formState.errors.firstName}>
              <FieldLabel htmlFor="firstName">First Name *</FieldLabel>
              <Input
                id="firstName"
                {...form.register('firstName')}
                placeholder="Enter first name"
                aria-invalid={!!form.formState.errors.firstName}
              />
              <FieldError>{form.formState.errors.firstName?.message}</FieldError>
            </Field>
            <Field data-invalid={!!form.formState.errors.lastName}>
              <FieldLabel htmlFor="lastName">Last Name *</FieldLabel>
              <Input
                id="lastName"
                {...form.register('lastName')}
                placeholder="Enter last name"
                aria-invalid={!!form.formState.errors.lastName}
              />
              <FieldError>{form.formState.errors.lastName?.message}</FieldError>
            </Field>
          </div>

          <Field data-invalid={!!form.formState.errors.email}>
            <FieldLabel htmlFor="email">Email Address *</FieldLabel>
            <Input
              id="email"
              type="email"
              {...form.register('email')}
              placeholder="Enter email address"
              aria-invalid={!!form.formState.errors.email}
            />
            <FieldError>{form.formState.errors.email?.message}</FieldError>
          </Field>

          <PhoneField
            control={form.control}
            name="phone"
            label="Phone Number"
            id="phone"
            purpose="account_contact"
            placeholder="Phone number"
          />

          <div className="flex justify-end">
            <Button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Profile'}
            </Button>
          </div>
        </form>

        {/* Danger Zone */}
        <div className="mt-12 pt-8 border-t border-red-100 dark:border-red-900/30">
          <h3 className="text-lg font-medium text-red-600 dark:text-red-400 mb-2">Danger Zone</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Once you delete your account, there is no going back. This will permanently remove all your data, including your academy, lessons, and bookings.
          </p>
          <Button
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-900/20"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete Account
          </Button>
        </div>
      </CardContent>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showAiAvatarModal}
        onClose={() => !aiGenerating && setShowAiAvatarModal(false)}
        title="AI Avatar (Beta)"
      >
        {aiOptions ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Generate a consistent cartoon tennis avatar with predefined options.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="ai-gender">Gender</FieldLabel>
                <select
                  id="ai-gender"
                  value={aiSelection.gender}
                  onChange={(e) => setAiSelection((prev) => ({ ...prev, gender: e.target.value as GenerateAiAvatarRequest['gender'] }))}
                  className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm"
                >
                  {aiOptions.genders.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field>
                <FieldLabel htmlFor="ai-pose">Pose</FieldLabel>
                <select
                  id="ai-pose"
                  value={aiSelection.pose}
                  onChange={(e) => setAiSelection((prev) => ({ ...prev, pose: e.target.value as GenerateAiAvatarRequest['pose'] }))}
                  className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm"
                >
                  {aiOptions.poses.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field>
                <FieldLabel htmlFor="ai-outfit">Outfit</FieldLabel>
                <select
                  id="ai-outfit"
                  value={aiSelection.outfit}
                  onChange={(e) => setAiSelection((prev) => ({ ...prev, outfit: e.target.value as GenerateAiAvatarRequest['outfit'] }))}
                  className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm"
                >
                  {aiOptions.outfits.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field>
                <FieldLabel htmlFor="ai-background">Background</FieldLabel>
                <select
                  id="ai-background"
                  value={aiSelection.background}
                  onChange={(e) => setAiSelection((prev) => ({ ...prev, background: e.target.value as GenerateAiAvatarRequest['background'] }))}
                  className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm"
                >
                  {aiOptions.backgrounds.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field>
                <FieldLabel htmlFor="ai-mood">Expression</FieldLabel>
                <select
                  id="ai-mood"
                  value={aiSelection.mood}
                  onChange={(e) => setAiSelection((prev) => ({ ...prev, mood: e.target.value as GenerateAiAvatarRequest['mood'] }))}
                  className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm"
                >
                  {aiOptions.moods.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={() => setShowAiAvatarModal(false)} disabled={aiGenerating}>
                Cancel
              </Button>
              <Button type="button" onClick={handleGenerateAiAvatar} disabled={aiGenerating}>
                {aiGenerating ? 'Generating AI Avatar...' : 'Generate AI Avatar'}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => !deleting && setShowDeleteModal(false)}
        title="Delete Your Account"
      >
        <div className="space-y-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                This is a permanent action!
              </p>
              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                All your data will be permanently deleted. If you are an academy owner, your academy and its Stripe Connect account will also be removed.
              </p>
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              To confirm, please type your email <span className="font-semibold text-gray-900 dark:text-white">{user?.email}</span> below:
            </p>
            <Input
              type="text"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder="Confirm email address"
              className="mt-1"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setShowDeleteModal(false);
                setConfirmEmail('');
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="bg-red-600 text-white hover:bg-red-700 hover:text-white border-none"
              onClick={handleDeleteAccount}
              disabled={deleting || confirmEmail !== user?.email}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Permanently Delete Account'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
    </>
  );
}
